import { Alert, Box, Stack } from "@mui/material";

import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import { DamageCostEvaluationDto } from "@/core/api/generated/v1";

import AppButton from "../../../Button/AppButton";
import { DamageCostEvaluationDamageCorrectionsDisplay } from "./DamageCostEvaluationDamageCorrectionsDisplay";

export interface DamageCostEvaluationApplyDamageCorrectionsProps {
  damageCostEvaluation: DamageCostEvaluationDto;
  onCancel: () => void;
  onSave: (newValue: DamageCostEvaluationDto) => void;
}

export function DamageCostEvaluationApplyDamageCorrections({
  damageCostEvaluation,
  onCancel,
  onSave,
}: DamageCostEvaluationApplyDamageCorrectionsProps) {
  const { enqueueSnackbar } = useAppSnackbar();

  const apply = async () => {
    try {
      const result =
        await apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsDamageCostEvaluationIdDamageCorrectionsApplyPost(
          {
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            damageCostEvaluationId: damageCostEvaluation.id || "",
          },
        );
      enqueueSnackbar("Damage corrections applied.", { variant: "success" });
      onSave(result.data);
    } catch (err) {
      const validation2 = ValidationHelper.handleApiErrorResponse(err);
      validation2.hasErrors &&
        enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
    }
  };

  return (
    <Stack gap={1}>
      {!damageCostEvaluation.corrections?.canApply && (
        <Alert severity='error'>{`Can't apply on the current stage.`}</Alert>
      )}

      {damageCostEvaluation.corrections?.canApply && (
        <Stack gap={1}>
          <Alert severity='info'>
            <Box>
              You are going to apply local damage corrections made in this operation to the vehicle
              actual state.
            </Box>
            <Box>
              The initial damages will be marked as inactive and corrected. The new damages will be
              created in the actual state.
            </Box>
          </Alert>

          <Alert severity='warning'>
            This action is irreversible. Please review it before proceeding.
          </Alert>

          <Box>
            <DamageCostEvaluationDamageCorrectionsDisplay
              damageCostEvaluation={damageCostEvaluation}
              corrections={damageCostEvaluation.corrections}
              displayProps={{
                alertsSection: true,
                overviewSection: true,
                damagesOnVisualModelsSection: true,
                appliedSection: false,
              }}
            />
          </Box>

          {/* Primary actions */}
          <Stack
            direction={{ xxs: "column", sm: "row" }}
            gap={1}
            sx={{
              justifyContent: { xxs: "flex-start", sm: "flex-end" },
              alignItems: { xxs: "flex-start", sm: "flex-start" },
            }}
          >
            <AppButton
              variant='outlined'
              size='medium'
              color='text'
              onClick={() => {
                onCancel();
              }}
            >
              Cancel
            </AppButton>

            <AppButton variant='contained' size='medium' color='primary' onClick={apply}>
              Apply
            </AppButton>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
